import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
} from "./business-operation.js";
import type {
  GroupBuyManualOperatorDecision,
  GroupBuyOperatorDecision,
} from "./group-buy-operator-decision.js";
import { GroupBuyManualOperatorModeSchema } from "./group-buy-operator-decision.js";
import {
  deriveOrderNumber,
  isOrderNumber,
  type OrderNumber,
  OrderNumberSchema,
} from "./order-number.js";

const IdempotencyKeySchema = Type.String({ minLength: 8, maxLength: 128 });
const MemberCreateIdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^\\S(?:[\\s\\S]*\\S)?$",
});
const MoneyFenSchema = Type.Integer({ minimum: 0, maximum: 100_000_000 });
const SignedMoneyFenSchema = Type.Integer({ minimum: -100_000_000, maximum: 100_000_000 });
const QuantitySchema = Type.Integer({ minimum: 0, maximum: 1_000_000 });
const SignedQuantitySchema = Type.Integer({ minimum: -1_000_000, maximum: 1_000_000 });
// A single staff-entered adjustment remains capped by SignedQuantitySchema, while
// the authoritative balance and immutable ledger must also represent converted
// purchase quantities accumulated across operations.
const InventoryLedgerQuantitySchema = Type.Integer({
  minimum: -1_000_000_000_000,
  maximum: 1_000_000_000_000,
});
const WalletAmountSchema = Type.Integer({ minimum: 1, maximum: 1_000_000_000_000 });
const SignedWalletAmountSchema = Type.Integer({
  minimum: -1_000_000_000_000,
  maximum: 1_000_000_000_000,
});
const ProductSkuIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
});

export const ProductUpsertRequestSchema = Type.Object(
  {
    skuId: ProductSkuIdSchema,
    storeProductCode: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    ),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    category: Type.String({ minLength: 1, maxLength: 40 }),
    unit: Type.String({ minLength: 1, maxLength: 16 }),
    unitPriceFen: MoneyFenSchema,
    costFen: MoneyFenSchema,
    barcode: Type.Union([
      Type.String({ minLength: 4, maxLength: 32, pattern: "^[0-9A-Za-z._-]+$" }),
      Type.Null(),
    ]),
    trackInventory: Type.Boolean(),
    lowStockThreshold: QuantitySchema,
    allowNegativeStock: Type.Boolean(),
    customerStorageEligible: Type.Optional(Type.Boolean()),
    customerRentalEnabled: Type.Optional(Type.Boolean()),
    customerRentalUnitPriceFen: Type.Optional(MoneyFenSchema),
    customerRentalTotalQuantity: Type.Optional(QuantitySchema),
    enabled: Type.Boolean(),
    expectedVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ProductUpsertRequest = Static<typeof ProductUpsertRequestSchema>;

const SupplierIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
});
const SupplierCanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const SupplierRequiredTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: SupplierCanonicalTextPattern });
const SupplierCodeSchema = Type.String({
  minLength: 1,
  maxLength: 40,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
});
const SupplierOptionalTextSchema = (maximum: number) =>
  Type.Union([SupplierRequiredTextSchema(maximum), Type.Null()]);

export const SupplierUpsertRequestSchema = Type.Object(
  {
    supplierId: SupplierIdSchema,
    supplierCode: SupplierCodeSchema,
    name: SupplierRequiredTextSchema(80),
    contactName: SupplierOptionalTextSchema(80),
    contactPhone: Type.Union([
      Type.String({
        minLength: 6,
        maxLength: 32,
        pattern: "^(?=.*[0-9])[0-9+()](?:[0-9+() -]*[0-9+()])?$",
      }),
      Type.Null(),
    ]),
    paymentTermsDays: Type.Integer({ minimum: 0, maximum: 3_650 }),
    notes: SupplierOptionalTextSchema(200),
    enabled: Type.Boolean(),
    expectedVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type SupplierUpsertRequest = Static<typeof SupplierUpsertRequestSchema>;

export function isSupplierUpsertRequest(value: unknown): value is SupplierUpsertRequest {
  return Value.Check(SupplierUpsertRequestSchema, value);
}

export const SupplierRecordSchema = Type.Object(
  {
    supplierId: SupplierIdSchema,
    supplierCode: SupplierCodeSchema,
    name: SupplierRequiredTextSchema(80),
    contactName: SupplierOptionalTextSchema(80),
    contactPhone: Type.Union([
      Type.String({
        minLength: 6,
        maxLength: 32,
        pattern: "^(?=.*[0-9])[0-9+()](?:[0-9+() -]*[0-9+()])?$",
      }),
      Type.Null(),
    ]),
    paymentTermsDays: Type.Integer({ minimum: 0, maximum: 3_650 }),
    notes: SupplierOptionalTextSchema(200),
    enabled: Type.Boolean(),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type SupplierRecord = Static<typeof SupplierRecordSchema>;

export const SupplierRecordListSchema = Type.Array(SupplierRecordSchema, { maxItems: 500 });

function supplierTextIsCanonical(value: string | null): boolean {
  return value === null || value === value.trim();
}

function supplierTimestampIsCanonical(value: string): boolean {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString() === value;
}

export function isSupplierRecord(value: unknown): value is SupplierRecord {
  if (!Value.Check(SupplierRecordSchema, value)) return false;
  const supplier = value as SupplierRecord;
  return (
    supplier.supplierCode === supplier.supplierCode.trim() &&
    supplier.name === supplier.name.trim() &&
    supplierTextIsCanonical(supplier.contactName) &&
    supplierTextIsCanonical(supplier.contactPhone) &&
    supplierTextIsCanonical(supplier.notes) &&
    supplierTimestampIsCanonical(supplier.createdAt) &&
    supplierTimestampIsCanonical(supplier.updatedAt) &&
    Date.parse(supplier.updatedAt) >= Date.parse(supplier.createdAt)
  );
}

export function isSupplierRecordList(value: unknown): value is SupplierRecord[] {
  if (!Value.Check(SupplierRecordListSchema, value)) return false;
  const supplierIds = new Set<string>();
  const supplierCodes = new Set<string>();
  for (const supplier of value) {
    if (!isSupplierRecord(supplier) || supplierIds.has(supplier.supplierId)) return false;
    const normalizedCode = supplier.supplierCode.toLowerCase();
    if (supplierCodes.has(normalizedCode)) return false;
    supplierIds.add(supplier.supplierId);
    supplierCodes.add(normalizedCode);
  }
  return true;
}

const ProductUnitConversionIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]*$",
});
const ProductUnitNameSchema = Type.String({
  minLength: 1,
  maxLength: 16,
  pattern: SupplierCanonicalTextPattern,
});
const ProductUnitBarcodeSchema = Type.Union([
  Type.String({ minLength: 4, maxLength: 32, pattern: "^[0-9A-Za-z._-]+$" }),
  Type.Null(),
]);

export const ProductUnitConversionUpsertRequestSchema = Type.Object(
  {
    skuId: ProductSkuIdSchema,
    conversionId: ProductUnitConversionIdSchema,
    unitName: ProductUnitNameSchema,
    baseUnitName: ProductUnitNameSchema,
    baseUnitQuantity: Type.Integer({ minimum: 2, maximum: 1_000_000 }),
    barcode: ProductUnitBarcodeSchema,
    enabled: Type.Boolean(),
    expectedProductVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    expectedVersion: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ProductUnitConversionUpsertRequest = Static<
  typeof ProductUnitConversionUpsertRequestSchema
>;

export function isProductUnitConversionUpsertRequest(
  value: unknown,
): value is ProductUnitConversionUpsertRequest {
  if (!Value.Check(ProductUnitConversionUpsertRequestSchema, value)) return false;
  return (
    value.unitName.toLocaleLowerCase("zh-CN") !== value.baseUnitName.toLocaleLowerCase("zh-CN")
  );
}

export const ProductUnitConversionRecordSchema = Type.Object(
  {
    skuId: ProductSkuIdSchema,
    conversionId: ProductUnitConversionIdSchema,
    unitName: ProductUnitNameSchema,
    baseUnitName: ProductUnitNameSchema,
    baseUnitQuantity: Type.Integer({ minimum: 2, maximum: 1_000_000 }),
    barcode: ProductUnitBarcodeSchema,
    enabled: Type.Boolean(),
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
export type ProductUnitConversionRecord = Static<typeof ProductUnitConversionRecordSchema>;

export const ProductUnitConversionRecordListSchema = Type.Array(ProductUnitConversionRecordSchema, {
  maxItems: 100,
});

export function isProductUnitConversionRecord(
  value: unknown,
): value is ProductUnitConversionRecord {
  if (!Value.Check(ProductUnitConversionRecordSchema, value)) return false;
  return (
    value.unitName === value.unitName.trim() &&
    value.baseUnitName === value.baseUnitName.trim() &&
    value.unitName.toLocaleLowerCase("zh-CN") !== value.baseUnitName.toLocaleLowerCase("zh-CN") &&
    supplierTimestampIsCanonical(value.createdAt) &&
    supplierTimestampIsCanonical(value.updatedAt) &&
    Date.parse(value.updatedAt) >= Date.parse(value.createdAt)
  );
}

export function isProductUnitConversionRecordList(
  value: unknown,
): value is ProductUnitConversionRecord[] {
  if (!Value.Check(ProductUnitConversionRecordListSchema, value)) return false;
  const identities = new Set<string>();
  const names = new Set<string>();
  const barcodes = new Set<string>();
  for (const conversion of value) {
    if (!isProductUnitConversionRecord(conversion)) return false;
    const identity = `${conversion.skuId}\n${conversion.conversionId}`;
    const name = `${conversion.skuId}\n${conversion.unitName.toLocaleLowerCase("zh-CN")}`;
    if (identities.has(identity) || names.has(name)) return false;
    if (conversion.barcode !== null) {
      if (barcodes.has(conversion.barcode)) return false;
      barcodes.add(conversion.barcode);
    }
    identities.add(identity);
    names.add(name);
  }
  return true;
}

export const InventoryOperationTypeSchema = Type.Union([
  Type.Literal("PURCHASE_IN"),
  Type.Literal("OTHER_IN"),
  Type.Literal("RETURN_OUT"),
  Type.Literal("LOSS_OUT"),
  Type.Literal("INTERNAL_USE_OUT"),
  Type.Literal("COUNT_SET"),
  Type.Literal("MANUAL_ADJUSTMENT"),
]);
export type InventoryOperationType = Static<typeof InventoryOperationTypeSchema>;

export const InventoryOperationApprovalPreconditionSchema = Type.Object(
  {
    productVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    inventoryVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    quantityBefore: SignedQuantitySchema,
  },
  { additionalProperties: false },
);
export type InventoryOperationApprovalPrecondition = Static<
  typeof InventoryOperationApprovalPreconditionSchema
>;

const InventoryOperationRequestProperties = {
  operationType: InventoryOperationTypeSchema,
  quantity: SignedQuantitySchema,
  unitCostFen: Type.Union([MoneyFenSchema, Type.Null()]),
  referenceNumber: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
  reason: Type.String({ minLength: 2, maxLength: 200 }),
  idempotencyKey: IdempotencyKeySchema,
} as const;

/**
 * Internal inventory mutation input. There is intentionally no public HTTP
 * writer for this shape: approval execution must use the stricter contract
 * below so a caller cannot omit the frozen approval facts.
 */
export const InventoryOperationRequestSchema = Type.Object(
  {
    ...InventoryOperationRequestProperties,
    operationTrace: Type.Optional(BusinessOperationTraceV1Schema),
    approvalPrecondition: Type.Optional(InventoryOperationApprovalPreconditionSchema),
  },
  { additionalProperties: false },
);
export type InventoryOperationRequest = Static<typeof InventoryOperationRequestSchema>;

export const ApprovedInventoryOperationRequestSchema = Type.Object(
  {
    ...InventoryOperationRequestProperties,
    approvalId: Type.String({ minLength: 1, maxLength: 128 }),
    skuId: Type.String({ minLength: 1, maxLength: 64 }),
    operationTrace: BusinessOperationTraceV1Schema,
    approvalPrecondition: InventoryOperationApprovalPreconditionSchema,
  },
  { additionalProperties: false },
);
export type ApprovedInventoryOperationRequest = Static<
  typeof ApprovedInventoryOperationRequestSchema
>;

export function isApprovedInventoryOperationRequest(
  value: unknown,
): value is ApprovedInventoryOperationRequest {
  return Value.Check(ApprovedInventoryOperationRequestSchema, value);
}

export const InventoryMovementTypeSchema = Type.Union([
  InventoryOperationTypeSchema,
  Type.Literal("PURCHASE_RECEIPT_IN"),
  Type.Literal("SALE_OUT"),
  Type.Literal("SALE_RETURN"),
]);

export const InventoryMovementRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 128 }),
    skuId: Type.String({ minLength: 1, maxLength: 64 }),
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    type: InventoryMovementTypeSchema,
    quantityDelta: InventoryLedgerQuantitySchema,
    quantityBefore: InventoryLedgerQuantitySchema,
    quantityAfter: InventoryLedgerQuantitySchema,
    unitCostFen: Type.Union([MoneyFenSchema, Type.Null()]),
    referenceNumber: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    reason: Type.String({ minLength: 1, maxLength: 200 }),
    actorId: Type.String({ minLength: 1, maxLength: 128 }),
    authorizerId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    sourceEventId: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
    operationTrace: Type.Optional(BusinessOperationTraceV1Schema),
  },
  { additionalProperties: false },
);
export type InventoryMovementRecord = Static<typeof InventoryMovementRecordSchema>;

export const InventoryMovementReadModelSchema = Type.Object(
  {
    skuId: Type.String({ minLength: 1, maxLength: 64 }),
    productName: Type.String({ minLength: 1, maxLength: 80 }),
    type: InventoryMovementTypeSchema,
    quantityDelta: InventoryLedgerQuantitySchema,
    quantityBefore: InventoryLedgerQuantitySchema,
    quantityAfter: InventoryLedgerQuantitySchema,
    unitCostFen: Type.Union([MoneyFenSchema, Type.Null()]),
    referenceNumber: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    reason: Type.String({ minLength: 1, maxLength: 200 }),
    operatorDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    authorizerDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
    dataSource: Type.Union([Type.Literal("TEST_DATA"), Type.Literal("STORE_RECORD")]),
  },
  { additionalProperties: false },
);
export type InventoryMovementReadModel = Static<typeof InventoryMovementReadModelSchema>;

const InventoryMovementRecordListSchema = Type.Array(InventoryMovementRecordSchema, {
  maxItems: 500,
});
const InventoryMovementReadModelListSchema = Type.Array(InventoryMovementReadModelSchema, {
  maxItems: 500,
});

export function isInventoryMovementRecordList(value: unknown): value is InventoryMovementRecord[] {
  if (!Value.Check(InventoryMovementRecordListSchema, value)) return false;
  const movementIds = new Set<string>();
  for (const movement of value) {
    if (movementIds.has(movement.id)) return false;
    movementIds.add(movement.id);
    if (movement.quantityAfter !== movement.quantityBefore + movement.quantityDelta) return false;
    if (!Number.isFinite(Date.parse(movement.occurredAt))) return false;
  }
  return true;
}

export function isInventoryMovementRecord(value: unknown): value is InventoryMovementRecord {
  return isInventoryMovementRecordList([value]);
}

export function isInventoryMovementReadModelList(
  value: unknown,
): value is InventoryMovementReadModel[] {
  if (!Value.Check(InventoryMovementReadModelListSchema, value)) return false;
  return value.every(
    (movement) =>
      movement.quantityAfter === movement.quantityBefore + movement.quantityDelta &&
      Number.isFinite(Date.parse(movement.occurredAt)),
  );
}

const SummaryMoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SummarySignedMoneyFenSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const SummaryCountSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const SummaryBusinessDateSchema = Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" });

export const ReconciliationSummaryReadModelSchema = Type.Object(
  {
    businessDate: SummaryBusinessDateSchema,
    generatedAt: Type.String({ minLength: 24, maxLength: 30 }),
    configuredManualPaymentMethods: Type.Object(
      {
        cash: Type.Boolean(),
        staticWechat: Type.Boolean(),
        staticAlipay: Type.Boolean(),
      },
      { additionalProperties: false },
    ),
    orders: SummaryCountSchema,
    offlineOrders: SummaryCountSchema,
    originalAmountFen: SummaryMoneyFenSchema,
    adjustmentsFen: SummaryMoneyFenSchema,
    adjustmentRecords: SummaryCountSchema,
    receivableFen: SummaryMoneyFenSchema,
    settledFen: SummaryMoneyFenSchema,
    cashFen: SummaryMoneyFenSchema,
    staticWechatFen: SummaryMoneyFenSchema,
    staticAlipayFen: SummaryMoneyFenSchema,
    pendingReconciliationFen: SummaryMoneyFenSchema,
    exceptionReconciliationFen: SummaryMoneyFenSchema,
    unsettledOrders: SummaryCountSchema,
    unsettledFen: SummaryMoneyFenSchema,
    unexplainedDifferenceFen: SummarySignedMoneyFenSchema,
    refundedFen: SummaryMoneyFenSchema,
    cashRefundedFen: SummaryMoneyFenSchema,
    staticWechatRefundedFen: SummaryMoneyFenSchema,
    staticAlipayRefundedFen: SummaryMoneyFenSchema,
    conflicts: SummaryCountSchema,
    evidence: Type.Literal("SIMULATED"),
  },
  { additionalProperties: false },
);
export type ReconciliationSummaryReadModel = Static<typeof ReconciliationSummaryReadModelSchema>;

export function isReconciliationSummaryReadModel(
  value: unknown,
): value is ReconciliationSummaryReadModel {
  if (!Value.Check(ReconciliationSummaryReadModelSchema, value)) return false;
  const businessDateMilliseconds = Date.parse(`${value.businessDate}T00:00:00.000Z`);
  if (
    !Number.isFinite(businessDateMilliseconds) ||
    new Date(businessDateMilliseconds).toISOString().slice(0, 10) !== value.businessDate
  ) {
    return false;
  }
  const generatedAtMilliseconds = Date.parse(value.generatedAt);
  if (
    !Number.isFinite(generatedAtMilliseconds) ||
    new Date(generatedAtMilliseconds).toISOString() !== value.generatedAt ||
    !value.configuredManualPaymentMethods.cash
  ) {
    return false;
  }
  const paymentChannelTotal = value.cashFen + value.staticWechatFen + value.staticAlipayFen;
  const refundChannelTotal =
    value.cashRefundedFen + value.staticWechatRefundedFen + value.staticAlipayRefundedFen;
  const expectedDifference =
    value.originalAmountFen -
    value.settledFen -
    value.pendingReconciliationFen -
    value.exceptionReconciliationFen -
    value.unsettledFen;
  return (
    value.offlineOrders <= value.orders &&
    value.unsettledOrders <= value.orders &&
    value.conflicts <= value.orders &&
    value.receivableFen === value.originalAmountFen - value.adjustmentsFen &&
    Number.isSafeInteger(paymentChannelTotal) &&
    paymentChannelTotal <= value.settledFen &&
    Number.isSafeInteger(refundChannelTotal) &&
    refundChannelTotal <= value.refundedFen &&
    Number.isSafeInteger(expectedDifference) &&
    value.unexplainedDifferenceFen === expectedDifference
  );
}

export const OwnerBusinessSummaryReadModelSchema = Type.Object(
  {
    businessDate: SummaryBusinessDateSchema,
    productRevenueFen: SummaryMoneyFenSchema,
    productCostFen: SummaryMoneyFenSchema,
    productGrossProfitFen: SummarySignedMoneyFenSchema,
    productQuantity: SummaryCountSchema,
    productCostKnownQuantity: SummaryCountSchema,
    missingCostSnapshots: Type.Array(
      Type.Object(
        {
          orderId: Type.String({ minLength: 1, maxLength: 128 }),
          tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
          skuId: Type.String({ minLength: 1, maxLength: 64 }),
          productName: Type.String({ minLength: 1, maxLength: 80 }),
          quantity: Type.Integer({ minimum: 1, maximum: 1_000_000 }),
          unitPriceFen: SummaryMoneyFenSchema,
          startedAt: Type.String({ minLength: 1, maxLength: 64 }),
        },
        { additionalProperties: false },
      ),
      { maxItems: 5_000 },
    ),
    commissionPayableFen: SummaryMoneyFenSchema,
    refundLossFen: SummaryMoneyFenSchema,
    lowStockProducts: SummaryCountSchema,
    outOfStockProducts: SummaryCountSchema,
    expectedCashFen: SummarySignedMoneyFenSchema,
    countedCashFen: Type.Union([SummaryMoneyFenSchema, Type.Null()]),
    cashDifferenceFen: Type.Union([SummarySignedMoneyFenSchema, Type.Null()]),
    evidence: Type.Literal("SIMULATED"),
  },
  { additionalProperties: false },
);
export type OwnerBusinessSummaryReadModel = Static<typeof OwnerBusinessSummaryReadModelSchema>;

function isCanonicalSummaryBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalSummaryTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

export function isOwnerBusinessSummaryReadModel(
  value: unknown,
): value is OwnerBusinessSummaryReadModel {
  if (!Value.Check(OwnerBusinessSummaryReadModelSchema, value)) return false;
  if (
    !isCanonicalSummaryBusinessDate(value.businessDate) ||
    value.productCostKnownQuantity > value.productQuantity ||
    value.productGrossProfitFen !== value.productRevenueFen - value.productCostFen ||
    !Number.isSafeInteger(value.productGrossProfitFen)
  ) {
    return false;
  }
  const listedMissingQuantity = value.missingCostSnapshots.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  if (
    !Number.isSafeInteger(listedMissingQuantity) ||
    listedMissingQuantity > value.productQuantity - value.productCostKnownQuantity ||
    !value.missingCostSnapshots.every((item) => isCanonicalSummaryTimestamp(item.startedAt))
  ) {
    return false;
  }
  return value.countedCashFen === null
    ? value.cashDifferenceFen === null
    : value.cashDifferenceFen !== null &&
        value.cashDifferenceFen === value.countedCashFen - value.expectedCashFen;
}

export interface InventoryProductRecord {
  skuId: string;
  storeProductCode?: string | null;
  version: number;
  name: string;
  category: string;
  unit: string;
  unitPriceFen: number;
  costFen: number;
  barcode: string | null;
  trackInventory: boolean;
  stockOnHand: number;
  lowStockThreshold: number;
  allowNegativeStock: boolean;
  customerStorageEligible?: boolean;
  customerRentalEnabled?: boolean;
  customerRentalUnitPriceFen?: number;
  customerRentalTotalQuantity?: number;
  enabled: boolean;
  lowStock: boolean;
  outOfStock: boolean;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

const inventoryProductRecordKeys = [
  "skuId",
  "storeProductCode",
  "version",
  "name",
  "category",
  "unit",
  "unitPriceFen",
  "costFen",
  "barcode",
  "trackInventory",
  "stockOnHand",
  "lowStockThreshold",
  "allowNegativeStock",
  "customerStorageEligible",
  "customerRentalEnabled",
  "customerRentalUnitPriceFen",
  "customerRentalTotalQuantity",
  "enabled",
  "lowStock",
  "outOfStock",
  "evidence",
] as const;

export function isInventoryProductRecord(value: unknown): value is InventoryProductRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).some(
      (key) =>
        !inventoryProductRecordKeys.includes(key as (typeof inventoryProductRecordKeys)[number]),
    ) ||
    !inventoryProductRecordKeys
      .filter(
        (key) =>
          key !== "customerStorageEligible" &&
          key !== "customerRentalEnabled" &&
          key !== "customerRentalUnitPriceFen" &&
          key !== "customerRentalTotalQuantity",
      )
      .every((key) => Object.hasOwn(record, key)) ||
    (Object.hasOwn(record, "customerStorageEligible") &&
      typeof record.customerStorageEligible !== "boolean") ||
    (Object.hasOwn(record, "customerRentalEnabled") &&
      typeof record.customerRentalEnabled !== "boolean") ||
    (Object.hasOwn(record, "customerRentalUnitPriceFen") &&
      (!Number.isSafeInteger(record.customerRentalUnitPriceFen) ||
        (record.customerRentalUnitPriceFen as number) < 0 ||
        (record.customerRentalUnitPriceFen as number) > 100_000_000)) ||
    (Object.hasOwn(record, "customerRentalTotalQuantity") &&
      (!Number.isSafeInteger(record.customerRentalTotalQuantity) ||
        (record.customerRentalTotalQuantity as number) < 0 ||
        (record.customerRentalTotalQuantity as number) > 1_000_000)) ||
    (record.customerRentalEnabled === true &&
      (!(record.customerRentalUnitPriceFen as number | undefined) ||
        !(record.customerRentalTotalQuantity as number | undefined)))
  ) {
    return false;
  }
  const skuIdValid =
    typeof record.skuId === "string" &&
    record.skuId.length >= 1 &&
    record.skuId.length <= 64 &&
    /^[A-Za-z0-9][A-Za-z0-9._-]*$/.test(record.skuId);
  const storeProductCodeValid =
    record.storeProductCode === null ||
    (typeof record.storeProductCode === "string" &&
      record.storeProductCode.length >= 1 &&
      record.storeProductCode.length <= 64);
  const barcodeValid =
    record.barcode === null ||
    (typeof record.barcode === "string" &&
      record.barcode.length >= 4 &&
      record.barcode.length <= 32 &&
      /^[0-9A-Za-z._-]+$/.test(record.barcode));
  return (
    skuIdValid &&
    storeProductCodeValid &&
    Number.isSafeInteger(record.version) &&
    (record.version as number) >= 1 &&
    typeof record.name === "string" &&
    record.name.length >= 1 &&
    record.name.length <= 80 &&
    typeof record.category === "string" &&
    record.category.length >= 1 &&
    record.category.length <= 40 &&
    typeof record.unit === "string" &&
    record.unit.length >= 1 &&
    record.unit.length <= 16 &&
    Number.isSafeInteger(record.unitPriceFen) &&
    (record.unitPriceFen as number) >= 0 &&
    (record.unitPriceFen as number) <= 100_000_000 &&
    Number.isSafeInteger(record.costFen) &&
    (record.costFen as number) >= 0 &&
    (record.costFen as number) <= 100_000_000 &&
    barcodeValid &&
    typeof record.trackInventory === "boolean" &&
    Number.isSafeInteger(record.stockOnHand) &&
    Number.isSafeInteger(record.lowStockThreshold) &&
    (record.lowStockThreshold as number) >= 0 &&
    (record.lowStockThreshold as number) <= 1_000_000 &&
    typeof record.allowNegativeStock === "boolean" &&
    typeof record.enabled === "boolean" &&
    record.lowStock ===
      (record.trackInventory &&
        (record.stockOnHand as number) <= (record.lowStockThreshold as number)) &&
    record.outOfStock === (record.trackInventory && (record.stockOnHand as number) <= 0) &&
    (record.evidence === "SIMULATED" || record.evidence === "STORE_ACCEPTED")
  );
}

export function isInventoryProductRecordList(value: unknown): value is InventoryProductRecord[] {
  if (!Array.isArray(value) || !value.every(isInventoryProductRecord)) return false;
  const skuIds = value.map((item) => item.skuId);
  const barcodes = value.flatMap((item) => (item.barcode === null ? [] : [item.barcode]));
  return new Set(skuIds).size === skuIds.length && new Set(barcodes).size === barcodes.length;
}

export const MemberStatusSchema = Type.Union([
  Type.Literal("ACTIVE"),
  Type.Literal("FROZEN"),
  Type.Literal("DISABLED"),
]);
export type MemberStatus = Static<typeof MemberStatusSchema>;

export const MemberCrmSourceSchema = Type.Union([
  Type.Literal("WALK_IN"),
  Type.Literal("MEMBER_REFERRAL"),
  Type.Literal("STAFF_REFERRAL"),
  Type.Literal("COACH_REFERRAL"),
  Type.Literal("GROUP_BUY"),
  Type.Literal("ONLINE"),
  Type.Literal("OTHER"),
]);
export type MemberCrmSource = Static<typeof MemberCrmSourceSchema>;

const MemberCrmPersonIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const MemberCrmNullableTextSchema = Type.Union([
  Type.String({ minLength: 1, maxLength: 80, pattern: "^\\S(?:[\\s\\S]*\\S)?$" }),
  Type.Null(),
]);
const MemberCrmBirthdaySchema = Type.Union([
  Type.String({ pattern: "^[0-9]{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$" }),
  Type.Null(),
]);
const MemberCrmPreferencesSchema = Type.Array(
  Type.String({ minLength: 1, maxLength: 40, pattern: "^\\S(?:[\\s\\S]*\\S)?$" }),
  { maxItems: 10, uniqueItems: true },
);

export const MemberCrmAssignmentInputSchema = Type.Union([
  Type.Object(
    {
      kind: Type.Literal("EMPLOYEE"),
      personId: MemberCrmPersonIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("COACH"),
      personId: MemberCrmPersonIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Null(),
]);
export type MemberCrmAssignmentInput = Static<typeof MemberCrmAssignmentInputSchema>;

export const MemberCrmProfileUpdateSchema = Type.Object(
  {
    source: MemberCrmSourceSchema,
    sourceDetail: MemberCrmNullableTextSchema,
    referrerName: MemberCrmNullableTextSchema,
    assignment: MemberCrmAssignmentInputSchema,
    levelLabel: Type.String({ maxLength: 40 }),
    birthday: MemberCrmBirthdaySchema,
    preferences: MemberCrmPreferencesSchema,
  },
  { additionalProperties: false },
);
export type MemberCrmProfileUpdate = Static<typeof MemberCrmProfileUpdateSchema>;

const referralMemberCrmSources = new Set<MemberCrmSource>([
  "MEMBER_REFERRAL",
  "STAFF_REFERRAL",
  "COACH_REFERRAL",
]);

function isRealCalendarDate(value: string): boolean {
  const [yearText, monthText, dayText] = value.split("-");
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

export function isMemberCrmProfileUpdate(value: unknown): value is MemberCrmProfileUpdate {
  if (!Value.Check(MemberCrmProfileUpdateSchema, value)) return false;
  if (value.birthday !== null && !isRealCalendarDate(value.birthday)) return false;
  return referralMemberCrmSources.has(value.source)
    ? value.referrerName !== null
    : value.referrerName === null;
}

const MemberCrmProfileSnapshotSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    phoneMasked: Type.String({ pattern: "^1[0-9]{2}\\*{4}[0-9]{4}$" }),
    status: MemberStatusSchema,
    source: MemberCrmSourceSchema,
    sourceDetail: MemberCrmNullableTextSchema,
    referrerName: MemberCrmNullableTextSchema,
    assignment: Type.Union([
      Type.Object(
        {
          kind: Type.Union([Type.Literal("EMPLOYEE"), Type.Literal("COACH")]),
          personId: MemberCrmPersonIdSchema,
          displayName: Type.String({ minLength: 1, maxLength: 120 }),
        },
        { additionalProperties: false },
      ),
      Type.Null(),
    ]),
    levelLabel: Type.String({ maxLength: 40 }),
    birthday: MemberCrmBirthdaySchema,
    preferences: MemberCrmPreferencesSchema,
    tags: Type.Array(Type.String({ minLength: 1, maxLength: 24 }), {
      maxItems: 10,
      uniqueItems: true,
    }),
    notes: Type.String({ maxLength: 500 }),
  },
  { additionalProperties: false },
);
export type MemberCrmProfileSnapshot = Static<typeof MemberCrmProfileSnapshotSchema>;

function isMemberCrmProfileSnapshotSemanticallyValid(value: MemberCrmProfileSnapshot): boolean {
  return (
    (value.birthday === null || isRealCalendarDate(value.birthday)) &&
    referralMemberCrmSources.has(value.source) === (value.referrerName !== null)
  );
}

function memberCrmAssignmentsEqual(
  left: MemberCrmProfileSnapshot["assignment"],
  right: MemberCrmProfileSnapshot["assignment"],
): boolean {
  return left === null || right === null
    ? left === right
    : left.kind === right.kind &&
        left.personId === right.personId &&
        left.displayName === right.displayName;
}

function memberCrmSnapshotsEqual(
  left: MemberCrmProfileSnapshot,
  right: MemberCrmProfileSnapshot,
): boolean {
  return (
    left.name === right.name &&
    left.phoneMasked === right.phoneMasked &&
    left.status === right.status &&
    left.source === right.source &&
    left.sourceDetail === right.sourceDetail &&
    left.referrerName === right.referrerName &&
    memberCrmAssignmentsEqual(left.assignment, right.assignment) &&
    left.levelLabel === right.levelLabel &&
    left.birthday === right.birthday &&
    JSON.stringify(left.preferences) === JSON.stringify(right.preferences) &&
    JSON.stringify(left.tags) === JSON.stringify(right.tags) &&
    left.notes === right.notes
  );
}

export const MemberCrmProfileChangeReadModelSchema = Type.Object(
  {
    changeNumber: Type.String({ pattern: "^CRM-[0-9]{8}-[0-9A-F]{12}$" }),
    versionBefore: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    versionAfter: Type.Integer({ minimum: 2, maximum: Number.MAX_SAFE_INTEGER }),
    changedFields: Type.Array(
      Type.Union([
        Type.Literal("NAME"),
        Type.Literal("PHONE"),
        Type.Literal("STATUS"),
        Type.Literal("SOURCE"),
        Type.Literal("REFERRER"),
        Type.Literal("ASSIGNMENT"),
        Type.Literal("LEVEL"),
        Type.Literal("BIRTHDAY"),
        Type.Literal("PREFERENCES"),
        Type.Literal("TAGS"),
        Type.Literal("NOTES"),
      ]),
      { minItems: 1, maxItems: 11, uniqueItems: true },
    ),
    before: MemberCrmProfileSnapshotSchema,
    after: MemberCrmProfileSnapshotSchema,
    actorDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
  },
  { additionalProperties: false },
);
export type MemberCrmProfileChangeReadModel = Static<typeof MemberCrmProfileChangeReadModelSchema>;

function memberCrmChangeMatchesSnapshots(change: MemberCrmProfileChangeReadModel): boolean {
  const changed = new Set(change.changedFields);
  const before = change.before;
  const after = change.after;
  const exactChecks: ReadonlyArray<
    readonly [MemberCrmProfileChangeReadModel["changedFields"][number], boolean]
  > = [
    ["NAME", before.name !== after.name],
    ["STATUS", before.status !== after.status],
    ["SOURCE", before.source !== after.source || before.sourceDetail !== after.sourceDetail],
    ["REFERRER", before.referrerName !== after.referrerName],
    ["ASSIGNMENT", !memberCrmAssignmentsEqual(before.assignment, after.assignment)],
    ["LEVEL", before.levelLabel !== after.levelLabel],
    ["BIRTHDAY", before.birthday !== after.birthday],
    ["PREFERENCES", JSON.stringify(before.preferences) !== JSON.stringify(after.preferences)],
    ["TAGS", JSON.stringify(before.tags) !== JSON.stringify(after.tags)],
    ["NOTES", before.notes !== after.notes],
  ];
  if (exactChecks.some(([field, differs]) => changed.has(field) !== differs)) return false;
  return before.phoneMasked === after.phoneMasked || changed.has("PHONE");
}

export const MemberCrmProfileReadModelSchema = Type.Object(
  {
    memberId: MemberCrmPersonIdSchema,
    memberNumber: Type.String({ minLength: 1, maxLength: 32 }),
    homeStoreId: MemberCrmPersonIdSchema,
    profile: MemberCrmProfileSnapshotSchema,
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    changes: Type.Array(MemberCrmProfileChangeReadModelSchema, { maxItems: 50 }),
  },
  { additionalProperties: false },
);
export type MemberCrmProfileReadModel = Static<typeof MemberCrmProfileReadModelSchema>;

export function isMemberCrmProfileReadModel(value: unknown): value is MemberCrmProfileReadModel {
  if (!Value.Check(MemberCrmProfileReadModelSchema, value)) return false;
  if (!isMemberCrmProfileSnapshotSemanticallyValid(value.profile)) return false;
  if (!isCanonicalMemberReadTimestamp(value.updatedAt)) return false;
  let previousVersionAfter = value.version + 1;
  let previousOccurredAt = Number.POSITIVE_INFINITY;
  let newerBefore: MemberCrmProfileSnapshot | null = null;
  for (const [index, change] of value.changes.entries()) {
    const occurredAt = Date.parse(change.occurredAt);
    if (
      !isMemberCrmProfileSnapshotSemanticallyValid(change.before) ||
      !isMemberCrmProfileSnapshotSemanticallyValid(change.after) ||
      !memberCrmChangeMatchesSnapshots(change) ||
      !isCanonicalMemberReadTimestamp(change.occurredAt) ||
      change.versionAfter !== change.versionBefore + 1 ||
      change.versionAfter > value.version ||
      change.versionAfter >= previousVersionAfter ||
      occurredAt > previousOccurredAt
    ) {
      return false;
    }
    if (
      (index === 0 && !memberCrmSnapshotsEqual(change.after, value.profile)) ||
      (newerBefore !== null && !memberCrmSnapshotsEqual(change.after, newerBefore))
    ) {
      return false;
    }
    previousVersionAfter = change.versionAfter;
    previousOccurredAt = occurredAt;
    newerBefore = change.before;
  }
  return true;
}

export const WalletUnitSchema = Type.Union([
  Type.Literal("CNY_FEN"),
  Type.Literal("POINT"),
  Type.Literal("MINUTE"),
  Type.Literal("COUNT"),
  Type.Literal("CUSTOM"),
]);
export type WalletUnit = Static<typeof WalletUnitSchema>;

export const WalletBusinessTypeSchema = Type.Union([
  Type.Literal("STORED_VALUE"),
  Type.Literal("PROMOTIONAL"),
  Type.Literal("LOYALTY"),
  Type.Literal("TIME_CREDIT"),
  Type.Literal("COUNT_CREDIT"),
  Type.Literal("CUSTOM"),
]);
export type WalletBusinessType = Static<typeof WalletBusinessTypeSchema>;

export const WalletPaymentTargetTypeSchema = Type.Union([
  Type.Literal("ORDER"),
  Type.Literal("PRODUCT"),
  Type.Literal("TABLE_TIME"),
  Type.Literal("COACH_SERVICE"),
]);
export type WalletPaymentTargetType = Static<typeof WalletPaymentTargetTypeSchema>;

export const WalletDefinitionUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 40 }),
    unit: WalletUnitSchema,
    customUnitLabel: Type.Union([Type.String({ minLength: 1, maxLength: 12 }), Type.Null()]),
    precision: Type.Integer({ minimum: 0, maximum: 4 }),
    businessType: WalletBusinessTypeSchema,
    refundable: Type.Boolean(),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WalletDefinitionUpsertRequest = Static<typeof WalletDefinitionUpsertRequestSchema>;

export interface WalletDefinitionRecord {
  id: string;
  name: string;
  unit: WalletUnit;
  customUnitLabel: string | null;
  precision: number;
  businessType: WalletBusinessType;
  refundable: boolean;
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

const WalletConfigurationIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$",
});
export const WalletDefinitionRecordSchema = Type.Object(
  {
    id: WalletConfigurationIdSchema,
    name: Type.String({ minLength: 1 }),
    unit: WalletUnitSchema,
    customUnitLabel: Type.Union([Type.String({ minLength: 1 }), Type.Null()]),
    precision: Type.Integer({ minimum: 0, maximum: 4 }),
    businessType: WalletBusinessTypeSchema,
    refundable: Type.Boolean(),
    enabled: Type.Boolean(),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
const WalletDefinitionRecordListSchema = Type.Array(WalletDefinitionRecordSchema, {
  maxItems: 2_000,
});

export function isWalletDefinitionRecordList(
  value: unknown,
  includeDisabled: boolean,
): value is WalletDefinitionRecord[] {
  if (!Value.Check(WalletDefinitionRecordListSchema, value)) return false;
  const ids = new Set<string>();
  for (const definition of value) {
    const nameLength = [...definition.name].length;
    const customUnitLabelLength =
      definition.customUnitLabel === null ? null : [...definition.customUnitLabel].length;
    if (
      ids.has(definition.id) ||
      nameLength > 40 ||
      (definition.unit === "CUSTOM") !== (definition.customUnitLabel !== null) ||
      (customUnitLabelLength !== null &&
        (definition.customUnitLabel?.trim().length === 0 || customUnitLabelLength > 12)) ||
      (!includeDisabled && !definition.enabled) ||
      !isCanonicalMemberReadTimestamp(definition.createdAt) ||
      !isCanonicalMemberReadTimestamp(definition.updatedAt) ||
      Date.parse(definition.updatedAt) < Date.parse(definition.createdAt)
    ) {
      return false;
    }
    ids.add(definition.id);
  }
  return true;
}

export interface WalletBalanceRecord {
  walletDefinitionId: string;
  walletName: string;
  unit: WalletUnit;
  customUnitLabel: string | null;
  precision: number;
  businessType: WalletBusinessType;
  balance: number;
  version: number;
  enabled: boolean;
}

export const WalletAllocationInputSchema = Type.Object(
  {
    walletDefinitionId: Type.String({ minLength: 1, maxLength: 64 }),
    amount: WalletAmountSchema,
  },
  { additionalProperties: false },
);
export type WalletAllocationInput = Static<typeof WalletAllocationInputSchema>;

export const WalletDisplayGroupUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 40 }),
    unit: WalletUnitSchema,
    presentation: Type.Union([
      Type.Literal("TOTAL_ONLY"),
      Type.Literal("TOTAL_WITH_DETAILS"),
      Type.Literal("DETAILS_ONLY"),
    ]),
    walletDefinitionIds: Type.Array(Type.String({ minLength: 1, maxLength: 64 }), {
      minItems: 1,
      maxItems: 20,
      uniqueItems: true,
    }),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WalletDisplayGroupUpsertRequest = Static<typeof WalletDisplayGroupUpsertRequestSchema>;

export interface WalletDisplayGroupRecord {
  id: string;
  name: string;
  unit: WalletUnit;
  presentation: "TOTAL_ONLY" | "TOTAL_WITH_DETAILS" | "DETAILS_ONLY";
  walletDefinitionIds: string[];
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const WalletDisplayGroupRecordSchema = Type.Object(
  {
    id: WalletConfigurationIdSchema,
    name: Type.String({ minLength: 1 }),
    unit: WalletUnitSchema,
    presentation: Type.Union([
      Type.Literal("TOTAL_ONLY"),
      Type.Literal("TOTAL_WITH_DETAILS"),
      Type.Literal("DETAILS_ONLY"),
    ]),
    walletDefinitionIds: Type.Array(WalletConfigurationIdSchema, {
      minItems: 1,
      maxItems: 20,
      uniqueItems: true,
    }),
    enabled: Type.Boolean(),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
const WalletDisplayGroupRecordListSchema = Type.Array(WalletDisplayGroupRecordSchema, {
  maxItems: 2_000,
});

export function isWalletDisplayGroupRecordList(
  value: unknown,
  includeDisabled: boolean,
): value is WalletDisplayGroupRecord[] {
  if (!Value.Check(WalletDisplayGroupRecordListSchema, value)) return false;
  const ids = new Set<string>();
  for (const group of value) {
    if (
      ids.has(group.id) ||
      [...group.name].length > 40 ||
      (!includeDisabled && !group.enabled) ||
      !isCanonicalMemberReadTimestamp(group.createdAt) ||
      !isCanonicalMemberReadTimestamp(group.updatedAt) ||
      Date.parse(group.updatedAt) < Date.parse(group.createdAt)
    ) {
      return false;
    }
    ids.add(group.id);
  }
  return true;
}

export const WalletPaymentRuleUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    mode: Type.Union([Type.Literal("PRIORITY"), Type.Literal("STRICT_RATIO")]),
    externalShortfallAllowed: Type.Boolean(),
    components: Type.Array(
      Type.Object(
        {
          walletDefinitionId: Type.String({ minLength: 1, maxLength: 64 }),
          priority: Type.Integer({ minimum: 1, maximum: 100 }),
          ratioBps: Type.Integer({ minimum: 0, maximum: 10_000 }),
          valueFenPerUnit: Type.Integer({ minimum: 1, maximum: 1_000_000 }),
          allowFallback: Type.Boolean(),
        },
        { additionalProperties: false },
      ),
      { minItems: 1, maxItems: 20 },
    ),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type WalletPaymentRuleUpsertRequest = Static<typeof WalletPaymentRuleUpsertRequestSchema>;

export interface WalletPaymentRuleRecord
  extends Omit<WalletPaymentRuleUpsertRequest, "expectedVersion" | "idempotencyKey"> {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const WalletPaymentRuleRecordSchema = Type.Object(
  {
    id: Type.String({ minLength: 1, maxLength: 128 }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    mode: Type.Union([Type.Literal("PRIORITY"), Type.Literal("STRICT_RATIO")]),
    externalShortfallAllowed: Type.Boolean(),
    components: Type.Array(
      Type.Object(
        {
          walletDefinitionId: Type.String({ minLength: 1, maxLength: 64 }),
          priority: Type.Integer({ minimum: 1, maximum: 100 }),
          ratioBps: Type.Integer({ minimum: 0, maximum: 10_000 }),
          valueFenPerUnit: Type.Integer({ minimum: 1, maximum: 1_000_000 }),
          allowFallback: Type.Boolean(),
        },
        { additionalProperties: false },
      ),
      { minItems: 1, maxItems: 20 },
    ),
    enabled: Type.Boolean(),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
const WalletPaymentRuleRecordListSchema = Type.Array(WalletPaymentRuleRecordSchema, {
  maxItems: 2_000,
});

export interface WalletPaymentTargetOption {
  targetType: WalletPaymentTargetType;
  targetId: string | null;
  label: string;
  priceFen: number | null;
  enabled: boolean;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const WalletPaymentTargetOptionSchema = Type.Object(
  {
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    label: Type.String({ minLength: 1, maxLength: 160 }),
    priceFen: Type.Union([MoneyFenSchema, Type.Null()]),
    enabled: Type.Boolean(),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
const WalletPaymentTargetOptionListSchema = Type.Array(WalletPaymentTargetOptionSchema, {
  maxItems: 10_000,
});

function walletPaymentTargetPairIsValid(
  targetType: WalletPaymentTargetType,
  targetId: string | null,
): boolean {
  return targetType === "ORDER" ? targetId === null : targetId !== null;
}

export function isWalletPaymentRuleRecordList(
  value: unknown,
  includeDisabled: boolean,
): value is WalletPaymentRuleRecord[] {
  if (!Value.Check(WalletPaymentRuleRecordListSchema, value)) return false;
  const ruleIds = new Set<string>();
  const enabledTargetKeys = new Set<string>();
  for (const rule of value) {
    const walletDefinitionIds = new Set<string>();
    const priorities = new Set<number>();
    let ratioTotal = 0;
    for (const component of rule.components) {
      if (
        walletDefinitionIds.has(component.walletDefinitionId) ||
        priorities.has(component.priority)
      ) {
        return false;
      }
      walletDefinitionIds.add(component.walletDefinitionId);
      priorities.add(component.priority);
      ratioTotal += component.ratioBps;
    }
    const enabledTargetKey = `${rule.targetType}\u0000${rule.targetId ?? ""}`;
    if (
      ruleIds.has(rule.id) ||
      (!includeDisabled && !rule.enabled) ||
      !walletPaymentTargetPairIsValid(rule.targetType, rule.targetId) ||
      (rule.enabled && enabledTargetKeys.has(enabledTargetKey)) ||
      !isCanonicalMemberReadTimestamp(rule.createdAt) ||
      !isCanonicalMemberReadTimestamp(rule.updatedAt) ||
      Date.parse(rule.updatedAt) < Date.parse(rule.createdAt) ||
      (rule.mode === "PRIORITY" && rule.components.some((component) => component.ratioBps !== 0)) ||
      (rule.mode === "STRICT_RATIO" &&
        (ratioTotal !== 10_000 || rule.components.some((component) => component.allowFallback)))
    ) {
      return false;
    }
    ruleIds.add(rule.id);
    if (rule.enabled) enabledTargetKeys.add(enabledTargetKey);
  }
  return true;
}

export function isWalletPaymentTargetOptionList(
  value: unknown,
): value is WalletPaymentTargetOption[] {
  if (!Value.Check(WalletPaymentTargetOptionListSchema, value)) return false;
  const targetKeys = new Set<string>();
  for (const target of value) {
    const targetKey = `${target.targetType}\u0000${target.targetId ?? ""}`;
    if (
      targetKeys.has(targetKey) ||
      !walletPaymentTargetPairIsValid(target.targetType, target.targetId) ||
      (target.targetType === "ORDER") !== (target.priceFen === null)
    ) {
      return false;
    }
    targetKeys.add(targetKey);
  }
  return true;
}

export const CreateMemberRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    phone: Type.String({ pattern: "^1[0-9]{10}$" }),
    memberNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
    notes: Type.String({ maxLength: 500 }),
    tags: Type.Array(Type.String({ minLength: 1, maxLength: 24 }), {
      maxItems: 10,
      uniqueItems: true,
    }),
    idempotencyKey: MemberCreateIdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateMemberRequest = Static<typeof CreateMemberRequestSchema>;

export function canonicalMemberCreateCommand(request: CreateMemberRequest): string {
  return JSON.stringify({
    name: request.name,
    phone: request.phone,
    memberNumber: request.memberNumber,
    notes: request.notes,
    tags: request.tags,
    idempotencyKey: request.idempotencyKey,
  });
}

export const MemberCreateAckSchema = Type.Object(
  {
    id: Type.String({
      minLength: 1,
      maxLength: 128,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
    }),
    memberNumber: Type.String({ minLength: 1, maxLength: 32 }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    status: Type.Literal("ACTIVE"),
    version: Type.Literal(1),
    commandSha256: Type.String({ pattern: "^[0-9a-f]{64}$" }),
  },
  { additionalProperties: false },
);
export type MemberCreateAck = Static<typeof MemberCreateAckSchema>;

export function isMemberCreateAck(value: unknown): value is MemberCreateAck {
  return Value.Check(MemberCreateAckSchema, value);
}

export const MemberCreatePendingSchema = Type.Object(
  {
    request: CreateMemberRequestSchema,
    commandSha256: Type.String({ pattern: "^[0-9a-f]{64}$" }),
    recordedAt: Type.String(),
  },
  { additionalProperties: false },
);
export type MemberCreatePending = Static<typeof MemberCreatePendingSchema>;

export function isMemberCreatePending(value: unknown): value is MemberCreatePending {
  if (!Value.Check(MemberCreatePendingSchema, value)) return false;
  const recordedAt = Date.parse(value.recordedAt);
  return Number.isFinite(recordedAt) && new Date(recordedAt).toISOString() === value.recordedAt;
}

export const UpdateMemberRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    phone: Type.String({ pattern: "^1[0-9]{10}$" }),
    notes: Type.String({ maxLength: 500 }),
    tags: Type.Array(Type.String({ minLength: 1, maxLength: 24 }), {
      maxItems: 10,
      uniqueItems: true,
    }),
    status: MemberStatusSchema,
    crmProfile: Type.Optional(MemberCrmProfileUpdateSchema),
    expectedVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER - 1 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type UpdateMemberRequest = Static<typeof UpdateMemberRequestSchema>;

export function canonicalMemberProfileUpdateCommand(request: UpdateMemberRequest): string {
  return JSON.stringify({
    name: request.name,
    phone: request.phone,
    notes: request.notes,
    tags: request.tags,
    status: request.status,
    ...(request.crmProfile === undefined ? {} : { crmProfile: request.crmProfile }),
    expectedVersion: request.expectedVersion,
    idempotencyKey: request.idempotencyKey,
  });
}

export const MemberProfileUpdateAckSchema = Type.Object(
  {
    id: Type.String({
      minLength: 1,
      maxLength: 128,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
    }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    status: MemberStatusSchema,
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    commandSha256: Type.String({ pattern: "^[0-9a-f]{64}$" }),
  },
  { additionalProperties: false },
);
export type MemberProfileUpdateAck = Static<typeof MemberProfileUpdateAckSchema>;

export function isMemberProfileUpdateAck(value: unknown): value is MemberProfileUpdateAck {
  return Value.Check(MemberProfileUpdateAckSchema, value);
}

export const MemberProfileUpdatePendingSchema = Type.Object(
  {
    memberId: Type.String({
      minLength: 1,
      maxLength: 128,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
    }),
    request: UpdateMemberRequestSchema,
    commandSha256: Type.String({ pattern: "^[0-9a-f]{64}$" }),
    recordedAt: Type.String(),
  },
  { additionalProperties: false },
);
export type MemberProfileUpdatePending = Static<typeof MemberProfileUpdatePendingSchema>;

export function isMemberProfileUpdatePending(value: unknown): value is MemberProfileUpdatePending {
  if (!Value.Check(MemberProfileUpdatePendingSchema, value)) return false;
  const recordedAt = Date.parse(value.recordedAt);
  return Number.isFinite(recordedAt) && new Date(recordedAt).toISOString() === value.recordedAt;
}

export interface MemberRecord {
  id: string;
  memberNumber: string;
  name: string;
  phoneMasked: string;
  phone?: string;
  status: MemberStatus;
  notes: string;
  tags: string[];
  principalBalanceFen: number;
  bonusBalanceFen: number;
  availableBalanceFen: number;
  totalRechargedFen: number;
  totalBonusFen: number;
  totalConsumedFen: number;
  visitCount: number;
  lastVisitedAt: string | null;
  version: number;
  walletVersion: number;
  wallets?: WalletBalanceRecord[];
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

const MemberReadIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const MemberReadMoneySchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const MemberReadCountSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const MemberReadVersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const WalletBalanceReadModelSchema = Type.Object(
  {
    walletDefinitionId: Type.String({
      minLength: 1,
      maxLength: 64,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$",
    }),
    walletName: Type.String({ minLength: 1, maxLength: 40 }),
    unit: WalletUnitSchema,
    customUnitLabel: Type.Union([Type.String({ minLength: 1, maxLength: 12 }), Type.Null()]),
    precision: Type.Integer({ minimum: 0, maximum: 4 }),
    businessType: WalletBusinessTypeSchema,
    balance: MemberReadMoneySchema,
    version: MemberReadVersionSchema,
    enabled: Type.Boolean(),
  },
  { additionalProperties: false },
);
const MemberReadModelProperties = {
  id: MemberReadIdSchema,
  memberNumber: Type.String({ minLength: 1, maxLength: 32 }),
  name: Type.String({ minLength: 1, maxLength: 80 }),
  phoneMasked: Type.String({ pattern: "^1[0-9]{2}\\*{4}[0-9]{4}$" }),
  status: MemberStatusSchema,
  notes: Type.String({ maxLength: 500 }),
  tags: Type.Array(Type.String({ minLength: 1, maxLength: 24 }), {
    maxItems: 10,
    uniqueItems: true,
  }),
  principalBalanceFen: MemberReadMoneySchema,
  bonusBalanceFen: MemberReadMoneySchema,
  availableBalanceFen: MemberReadMoneySchema,
  totalRechargedFen: MemberReadMoneySchema,
  totalBonusFen: MemberReadMoneySchema,
  totalConsumedFen: MemberReadMoneySchema,
  visitCount: MemberReadCountSchema,
  lastVisitedAt: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
  version: MemberReadVersionSchema,
  walletVersion: MemberReadVersionSchema,
  wallets: Type.Array(WalletBalanceReadModelSchema, { maxItems: 2_000 }),
  createdAt: Type.String({ minLength: 1, maxLength: 64 }),
  updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
  evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
} as const;

export const MemberReadModelSchema = Type.Object(MemberReadModelProperties, {
  additionalProperties: false,
});
export type MemberReadModel = Static<typeof MemberReadModelSchema>;

const MemberRecordReadSchema = Type.Object(
  {
    ...MemberReadModelProperties,
    phone: Type.Optional(Type.String({ pattern: "^1[0-9]{10}$" })),
  },
  { additionalProperties: false },
);
const MemberReadModelListSchema = Type.Array(MemberReadModelSchema, { maxItems: 200 });
const MemberRecordReadListSchema = Type.Array(MemberRecordReadSchema, { maxItems: 200 });

function isCanonicalMemberReadTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function memberReadSemanticsAreConsistent(
  member: MemberReadModel,
  expectedMemberId?: string,
): boolean {
  if (expectedMemberId !== undefined && member.id !== expectedMemberId) return false;
  if (
    !isCanonicalMemberReadTimestamp(member.createdAt) ||
    !isCanonicalMemberReadTimestamp(member.updatedAt) ||
    Date.parse(member.updatedAt) < Date.parse(member.createdAt) ||
    (member.lastVisitedAt !== null && !isCanonicalMemberReadTimestamp(member.lastVisitedAt))
  ) {
    return false;
  }
  const walletIds = new Set<string>();
  let availableBalanceFen = 0;
  for (const wallet of member.wallets) {
    if (
      walletIds.has(wallet.walletDefinitionId) ||
      (wallet.unit === "CUSTOM") !== (wallet.customUnitLabel !== null)
    ) {
      return false;
    }
    walletIds.add(wallet.walletDefinitionId);
    if (wallet.unit === "CNY_FEN") {
      availableBalanceFen += wallet.balance;
      if (!Number.isSafeInteger(availableBalanceFen)) return false;
    }
  }
  return availableBalanceFen === member.availableBalanceFen;
}

function memberReadListSemanticsAreConsistent(value: MemberReadModel[]): boolean {
  const ids = new Set<string>();
  const memberNumbers = new Set<string>();
  let previousUpdatedAt = Number.POSITIVE_INFINITY;
  for (const member of value) {
    const updatedAt = Date.parse(member.updatedAt);
    if (
      ids.has(member.id) ||
      memberNumbers.has(member.memberNumber) ||
      !memberReadSemanticsAreConsistent(member) ||
      updatedAt > previousUpdatedAt
    ) {
      return false;
    }
    ids.add(member.id);
    memberNumbers.add(member.memberNumber);
    previousUpdatedAt = updatedAt;
  }
  return true;
}

export function isMemberRecord(value: unknown, expectedMemberId?: string): value is MemberRecord {
  return (
    Value.Check(MemberRecordReadSchema, value) &&
    memberReadSemanticsAreConsistent(value, expectedMemberId)
  );
}

export function isMemberRecordList(value: unknown): value is MemberRecord[] {
  return (
    Value.Check(MemberRecordReadListSchema, value) && memberReadListSemanticsAreConsistent(value)
  );
}

export function isMemberReadModel(
  value: unknown,
  expectedMemberId?: string,
): value is MemberReadModel {
  return (
    Value.Check(MemberReadModelSchema, value) &&
    memberReadSemanticsAreConsistent(value, expectedMemberId)
  );
}

export function isMemberReadModelList(value: unknown): value is MemberReadModel[] {
  return (
    Value.Check(MemberReadModelListSchema, value) && memberReadListSemanticsAreConsistent(value)
  );
}

export const MemberSensitiveProfileSchema = Type.Object(
  {
    id: Type.String({
      minLength: 1,
      maxLength: 128,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
    }),
    phone: Type.String({ pattern: "^1[0-9]{10}$" }),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  },
  { additionalProperties: false },
);
export type MemberSensitiveProfile = Static<typeof MemberSensitiveProfileSchema>;

export function isMemberSensitiveProfile(value: unknown): value is MemberSensitiveProfile {
  return Value.Check(MemberSensitiveProfileSchema, value);
}

export const RechargePlanUpsertRequestSchema = Type.Object(
  {
    name: Type.String({ minLength: 1, maxLength: 80 }),
    rechargeAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    bonusAmountFen: Type.Optional(MoneyFenSchema),
    allocations: Type.Optional(
      Type.Array(WalletAllocationInputSchema, { minItems: 1, maxItems: 20 }),
    ),
    description: Type.String({ maxLength: 200 }),
    enabled: Type.Boolean(),
    validFrom: Type.String({ format: "date-time" }),
    validUntil: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RechargePlanUpsertRequest = Static<typeof RechargePlanUpsertRequestSchema>;

export interface RechargePlanExpectedRecharge {
  paidAmountFen: number;
  principalFen: number;
  bonusFen: number;
  allocations: WalletAllocationInput[];
}

export const RechargePlanExpectedRechargeSchema = Type.Object(
  {
    paidAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    principalFen: MoneyFenSchema,
    bonusFen: MoneyFenSchema,
    allocations: Type.Array(WalletAllocationInputSchema, { minItems: 1, maxItems: 20 }),
  },
  { additionalProperties: false },
);

export interface RechargePlanRecord {
  id: string;
  name: string;
  rechargeAmountFen: number;
  bonusAmountFen: number;
  allocations?: WalletAllocationInput[];
  expectedRecharge: RechargePlanExpectedRecharge;
  description: string;
  enabled: boolean;
  validFrom: string;
  validUntil: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const RechargePlanRecordSchema = Type.Object(
  {
    id: WalletConfigurationIdSchema,
    name: Type.String({ minLength: 1, maxLength: 80 }),
    rechargeAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    bonusAmountFen: MoneyFenSchema,
    allocations: Type.Array(WalletAllocationInputSchema, { minItems: 1, maxItems: 20 }),
    expectedRecharge: RechargePlanExpectedRechargeSchema,
    description: Type.String({ maxLength: 200 }),
    enabled: Type.Boolean(),
    validFrom: Type.String({ minLength: 1, maxLength: 64 }),
    validUntil: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    version: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    updatedAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
const RechargePlanRecordListSchema = Type.Array(RechargePlanRecordSchema, { maxItems: 2_000 });

export function isRechargePlanRecordList(
  value: unknown,
  includeDisabled: boolean,
): value is RechargePlanRecord[] {
  if (!Value.Check(RechargePlanRecordListSchema, value)) return false;
  const planIds = new Set<string>();
  for (const plan of value) {
    const validFrom = Date.parse(plan.validFrom);
    const validUntil = plan.validUntil === null ? null : Date.parse(plan.validUntil);
    const walletIds = new Set<string>();
    const expectedWalletIds = new Set<string>();
    if (
      planIds.has(plan.id) ||
      (!includeDisabled && !plan.enabled) ||
      !isCanonicalMemberReadTimestamp(plan.validFrom) ||
      (plan.validUntil !== null && !isCanonicalMemberReadTimestamp(plan.validUntil)) ||
      !isCanonicalMemberReadTimestamp(plan.createdAt) ||
      !isCanonicalMemberReadTimestamp(plan.updatedAt) ||
      (validUntil !== null && validUntil <= validFrom) ||
      Date.parse(plan.updatedAt) < Date.parse(plan.createdAt)
    ) {
      return false;
    }
    for (const allocation of plan.allocations ?? []) {
      if (walletIds.has(allocation.walletDefinitionId)) return false;
      walletIds.add(allocation.walletDefinitionId);
    }
    if (
      plan.expectedRecharge.paidAmountFen !== plan.rechargeAmountFen ||
      plan.expectedRecharge.bonusFen !== plan.bonusAmountFen ||
      plan.expectedRecharge.allocations.length !== (plan.allocations ?? []).length
    ) {
      return false;
    }
    for (const [index, allocation] of plan.expectedRecharge.allocations.entries()) {
      const configured = plan.allocations?.[index];
      if (
        expectedWalletIds.has(allocation.walletDefinitionId) ||
        configured?.walletDefinitionId !== allocation.walletDefinitionId ||
        configured.amount !== allocation.amount
      ) {
        return false;
      }
      expectedWalletIds.add(allocation.walletDefinitionId);
    }
    planIds.add(plan.id);
  }
  return true;
}

export const MemberRechargeRequestSchema = Type.Object(
  {
    planId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    expectedPlanVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    customPrincipalFen: Type.Union([
      Type.Integer({ minimum: 1, maximum: 100_000_000 }),
      Type.Null(),
    ]),
    customAllocations: Type.Optional(
      Type.Union([
        Type.Array(WalletAllocationInputSchema, { minItems: 1, maxItems: 20 }),
        Type.Null(),
      ]),
    ),
    method: Type.Union([
      Type.Literal("CASH"),
      Type.Literal("STATIC_WECHAT"),
      Type.Literal("STATIC_ALIPAY"),
    ]),
    note: Type.String({ maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberRechargeRequest = Static<typeof MemberRechargeRequestSchema>;

export const ReconcileMemberRechargeRequestSchema = Type.Object(
  {
    outcome: Type.Union([
      Type.Literal("CONFIRMED"),
      Type.Literal("NOT_RECEIVED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_PAYMENT"),
    ]),
    note: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReconcileMemberRechargeRequest = Static<typeof ReconcileMemberRechargeRequestSchema>;

export interface MemberRechargeRecord {
  id: string;
  memberId: string;
  memberName: string;
  planId: string | null;
  paidAmountFen?: number;
  principalFen: number;
  bonusFen: number;
  allocations?: WalletAllocationInput[];
  method: "CASH" | "STATIC_WECHAT" | "STATIC_ALIPAY";
  status:
    | "CONFIRMED_LOCAL"
    | "PENDING_RECONCILIATION"
    | "CONFIRMED_RECONCILED"
    | "RECONCILIATION_EXCEPTION"
    | "RECONCILIATION_CLOSED";
  note: string;
  createdAt: string;
  reconciledAt: string | null;
  evidence: "MANUAL_FALLBACK" | "STORE_ACCEPTED";
}

export const MemberRechargeRecordSchema = Type.Object(
  {
    id: Type.String({
      minLength: 1,
      maxLength: 128,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
    }),
    memberId: MemberReadIdSchema,
    memberName: Type.String({ minLength: 1, maxLength: 80 }),
    planId: Type.Union([
      Type.String({
        minLength: 1,
        maxLength: 64,
        pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,63}$",
      }),
      Type.Null(),
    ]),
    paidAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    principalFen: MoneyFenSchema,
    bonusFen: MoneyFenSchema,
    allocations: Type.Array(WalletAllocationInputSchema, { minItems: 1, maxItems: 20 }),
    method: Type.Union([
      Type.Literal("CASH"),
      Type.Literal("STATIC_WECHAT"),
      Type.Literal("STATIC_ALIPAY"),
    ]),
    status: Type.Union([
      Type.Literal("CONFIRMED_LOCAL"),
      Type.Literal("PENDING_RECONCILIATION"),
      Type.Literal("CONFIRMED_RECONCILED"),
      Type.Literal("RECONCILIATION_EXCEPTION"),
      Type.Literal("RECONCILIATION_CLOSED"),
    ]),
    note: Type.String({ maxLength: 500 }),
    createdAt: Type.String({ minLength: 1, maxLength: 64 }),
    reconciledAt: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    evidence: Type.Union([Type.Literal("MANUAL_FALLBACK"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);
const MemberRechargeRecordListSchema = Type.Array(MemberRechargeRecordSchema, { maxItems: 300 });

export function isMemberRechargeRecordList(
  value: unknown,
  pendingOnly: boolean,
): value is MemberRechargeRecord[] {
  if (!Value.Check(MemberRechargeRecordListSchema, value)) return false;
  const rechargeIds = new Set<string>();
  let previousCreatedAt = Number.POSITIVE_INFINITY;
  for (const recharge of value) {
    const createdAt = Date.parse(recharge.createdAt);
    const reconciledAt = recharge.reconciledAt === null ? null : Date.parse(recharge.reconciledAt);
    const isStaticPayment =
      recharge.method === "STATIC_WECHAT" || recharge.method === "STATIC_ALIPAY";
    const hasReconciliation = reconciledAt !== null;
    if (
      rechargeIds.has(recharge.id) ||
      !isCanonicalMemberReadTimestamp(recharge.createdAt) ||
      (recharge.reconciledAt !== null && !isCanonicalMemberReadTimestamp(recharge.reconciledAt)) ||
      createdAt > previousCreatedAt ||
      (reconciledAt !== null && reconciledAt < createdAt) ||
      (recharge.status === "CONFIRMED_LOCAL" &&
        (recharge.method !== "CASH" || hasReconciliation)) ||
      (recharge.status === "PENDING_RECONCILIATION" && (!isStaticPayment || hasReconciliation)) ||
      ((recharge.status === "CONFIRMED_RECONCILED" ||
        recharge.status === "RECONCILIATION_EXCEPTION" ||
        recharge.status === "RECONCILIATION_CLOSED") &&
        (!isStaticPayment || !hasReconciliation)) ||
      (pendingOnly &&
        recharge.status !== "PENDING_RECONCILIATION" &&
        recharge.status !== "RECONCILIATION_EXCEPTION")
    ) {
      return false;
    }
    const walletIds = new Set<string>();
    for (const allocation of recharge.allocations ?? []) {
      if (walletIds.has(allocation.walletDefinitionId)) return false;
      walletIds.add(allocation.walletDefinitionId);
    }
    rechargeIds.add(recharge.id);
    previousCreatedAt = createdAt;
  }
  return true;
}

export interface MemberFundTransactionRecord {
  id: string;
  memberId: string;
  type: "RECHARGE" | "CONSUMPTION" | "REFUND" | "ADJUSTMENT";
  principalDeltaFen: number;
  bonusDeltaFen: number;
  principalBalanceFen: number;
  bonusBalanceFen: number;
  entries?: WalletLedgerEntryRecord[];
  paymentRuleId?: string | null;
  paymentRuleVersion?: number | null;
  targetType?: WalletPaymentTargetType | null;
  targetId?: string | null;
  priceSnapshotFen?: number;
  walletCoveredAmountFen?: number;
  externalAmountFen?: number;
  ruleApplications?: WalletRuleApplicationRecord[];
  orderId: string | null;
  orderNumber: OrderNumber | null;
  rechargeId: string | null;
  originalTransactionId: string | null;
  reason: string;
  actorId: string;
  authorizerId: string | null;
  occurredAt: string;
  evidence: "SIMULATED" | "MANUAL_FALLBACK" | "STORE_ACCEPTED";
}

export interface WalletRuleApplicationRecord {
  targetType: WalletPaymentTargetType;
  targetId: string | null;
  amountFen: number;
  paymentRuleId: string;
  paymentRuleVersion: number;
  walletCoveredAmountFen: number;
  externalAmountFen: number;
}

export const MemberSpendLineSchema = Type.Object(
  {
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
  },
  { additionalProperties: false },
);
export type MemberSpendLine = Static<typeof MemberSpendLineSchema>;

export const MemberSpendRequestSchema = Type.Object(
  {
    memberId: Type.String({ minLength: 1 }),
    orderId: Type.String({ minLength: 1 }),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    paymentRuleId: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    ),
    targetType: Type.Optional(WalletPaymentTargetTypeSchema),
    targetId: Type.Optional(
      Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    ),
    allowExternalShortfall: Type.Optional(Type.Boolean()),
    lines: Type.Optional(Type.Array(MemberSpendLineSchema, { minItems: 1, maxItems: 100 })),
    expectedPlanHash: Type.Optional(Type.String({ pattern: "^[a-f0-9]{64}$" })),
    idempotencyKey: IdempotencyKeySchema,
    operationTrace: Type.Optional(BusinessOperationTraceV1Schema),
  },
  { additionalProperties: false },
);
export type MemberSpendRequest = Static<typeof MemberSpendRequestSchema>;

export interface MemberSpendResult {
  transaction: MemberFundTransactionRecord;
  member: MemberRecord;
  operationTrace?: BusinessOperationTraceV1;
  audit?: {
    id: string;
    action: "MEMBER_BALANCE_REFUNDED";
    status: "RECORDED";
  };
}

export interface MemberRefundStatusResult {
  requestId: string;
  status: "NOT_FOUND" | "SUCCEEDED";
  receiptVersion: 1 | 2 | 3 | null;
  result: MemberSpendResult | null;
}

export const MemberRefundWalletAllocationSchema = Type.Object(
  {
    walletDefinitionId: Type.String({
      minLength: 1,
      maxLength: 64,
      pattern: "^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$",
    }),
    walletName: Type.String({ minLength: 1, maxLength: 80 }),
    unit: WalletUnitSchema,
    customUnitLabel: Type.Union([Type.String({ minLength: 1, maxLength: 12 }), Type.Null()]),
    precision: Type.Integer({ minimum: 0, maximum: 4 }),
    refundUnits: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    refundAmountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
  },
  { additionalProperties: false },
);
export type MemberRefundWalletAllocation = Static<typeof MemberRefundWalletAllocationSchema>;

export const MemberRefundWalletAllocationsSchema = Type.Array(MemberRefundWalletAllocationSchema, {
  maxItems: 100,
});

export const MemberRefundRequestSchema = Type.Object(
  {
    memberId: Type.String({ minLength: 1 }),
    orderId: Type.String({ minLength: 1 }),
    originalTransactionId: Type.String({ minLength: 1 }),
    amountFen: Type.Integer({ minimum: 1, maximum: 100_000_000 }),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    idempotencyKey: IdempotencyKeySchema,
    operationTrace: Type.Optional(BusinessOperationTraceV1Schema),
    expectedRefundAllocations: Type.Optional(
      Type.Array(MemberRefundWalletAllocationSchema, { minItems: 1, maxItems: 100 }),
    ),
  },
  { additionalProperties: false },
);
export type MemberRefundRequest = Static<typeof MemberRefundRequestSchema>;

export function isMemberRefundRequest(value: unknown): value is MemberRefundRequest {
  return Value.Check(MemberRefundRequestSchema, value);
}

export const MemberAdjustmentRequestSchema = Type.Object(
  {
    principalDeltaFen: Type.Optional(SignedMoneyFenSchema),
    bonusDeltaFen: Type.Optional(SignedMoneyFenSchema),
    entries: Type.Optional(
      Type.Array(
        Type.Object(
          {
            walletDefinitionId: Type.String({ minLength: 1, maxLength: 64 }),
            delta: SignedWalletAmountSchema,
          },
          { additionalProperties: false },
        ),
        { minItems: 1, maxItems: 20 },
      ),
    ),
    reason: Type.String({ minLength: 2, maxLength: 200 }),
    expectedWalletVersion: Type.Integer({ minimum: 1 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type MemberAdjustmentRequest = Static<typeof MemberAdjustmentRequestSchema>;

export interface WalletLedgerEntryRecord {
  id: string;
  transactionId: string;
  walletDefinitionId: string;
  walletName: string;
  unit: WalletUnit;
  customUnitLabel?: string | null;
  precision?: number;
  delta: number;
  balanceBefore: number;
  balanceAfter: number;
  coveredAmountFen: number | null;
  refundableAtTransaction?: boolean;
}

export interface MemberFundTransactionEntryReadModel {
  walletDefinitionId: string;
  walletName: string;
  unit: WalletUnit;
  customUnitLabel: string | null;
  precision: number;
  delta: number;
  balanceBefore: number;
  balanceAfter: number;
  coveredAmountFen: number | null;
  refundableAtTransaction: boolean;
}

export interface MemberFundTransactionReadModel {
  id: string;
  memberId: string;
  type: MemberFundTransactionRecord["type"];
  principalDeltaFen: number;
  bonusDeltaFen: number;
  principalBalanceFen: number;
  bonusBalanceFen: number;
  entries: MemberFundTransactionEntryReadModel[];
  priceSnapshotFen: number;
  walletCoveredAmountFen: number;
  externalAmountFen: number;
  orderNumber: OrderNumber | null;
  rechargeId: string | null;
  originalTransactionId: string | null;
  reason: string;
  operatorDisplayName: string;
  authorizerDisplayName: string | null;
  occurredAt: string;
  dataSource: "TEST_DATA" | "STORE_RECORD";
}

const FundIdentifierSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const FundWalletIdentifierSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$",
});
const FundSafeIntegerSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const FundBalanceSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const FundNullableIdentifierSchema = Type.Union([FundIdentifierSchema, Type.Null()]);
const FundTransactionTypeSchema = Type.Union([
  Type.Literal("RECHARGE"),
  Type.Literal("CONSUMPTION"),
  Type.Literal("REFUND"),
  Type.Literal("ADJUSTMENT"),
]);
const FundTransactionEntryContentSchema = {
  walletDefinitionId: FundWalletIdentifierSchema,
  walletName: Type.String({ minLength: 1, maxLength: 40 }),
  unit: WalletUnitSchema,
  customUnitLabel: Type.Union([Type.String({ minLength: 1, maxLength: 12 }), Type.Null()]),
  precision: Type.Integer({ minimum: 0, maximum: 4 }),
  delta: FundSafeIntegerSchema,
  balanceBefore: FundBalanceSchema,
  balanceAfter: FundBalanceSchema,
  coveredAmountFen: Type.Union([FundBalanceSchema, Type.Null()]),
  refundableAtTransaction: Type.Boolean(),
} as const;
const FundTransactionSourceEntrySchema = Type.Object(
  {
    id: FundIdentifierSchema,
    transactionId: FundIdentifierSchema,
    ...FundTransactionEntryContentSchema,
  },
  { additionalProperties: false },
);
const FundTransactionReadEntrySchema = Type.Object(FundTransactionEntryContentSchema, {
  additionalProperties: false,
});
const FundRuleApplicationSchema = Type.Object(
  {
    targetType: WalletPaymentTargetTypeSchema,
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    amountFen: FundBalanceSchema,
    paymentRuleId: FundWalletIdentifierSchema,
    paymentRuleVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    walletCoveredAmountFen: FundBalanceSchema,
    externalAmountFen: FundBalanceSchema,
  },
  { additionalProperties: false },
);
const MemberFundTransactionRecordBoundarySchema = Type.Object(
  {
    id: FundIdentifierSchema,
    memberId: FundIdentifierSchema,
    type: FundTransactionTypeSchema,
    principalDeltaFen: FundSafeIntegerSchema,
    bonusDeltaFen: FundSafeIntegerSchema,
    principalBalanceFen: FundBalanceSchema,
    bonusBalanceFen: FundBalanceSchema,
    entries: Type.Array(FundTransactionSourceEntrySchema, { maxItems: 2_000 }),
    paymentRuleId: Type.Union([FundWalletIdentifierSchema, Type.Null()]),
    paymentRuleVersion: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    targetType: Type.Union([WalletPaymentTargetTypeSchema, Type.Null()]),
    targetId: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    priceSnapshotFen: FundBalanceSchema,
    walletCoveredAmountFen: FundBalanceSchema,
    externalAmountFen: FundBalanceSchema,
    ruleApplications: Type.Array(FundRuleApplicationSchema, { maxItems: 100 }),
    orderId: FundNullableIdentifierSchema,
    orderNumber: Type.Union([OrderNumberSchema, Type.Null()]),
    rechargeId: FundNullableIdentifierSchema,
    originalTransactionId: FundNullableIdentifierSchema,
    reason: Type.String({ minLength: 1, maxLength: 200 }),
    actorId: FundIdentifierSchema,
    authorizerId: FundNullableIdentifierSchema,
    occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
    evidence: Type.Union([
      Type.Literal("SIMULATED"),
      Type.Literal("MANUAL_FALLBACK"),
      Type.Literal("STORE_ACCEPTED"),
    ]),
  },
  { additionalProperties: false },
);
const MemberFundTransactionReadModelSchema = Type.Object(
  {
    id: FundIdentifierSchema,
    memberId: FundIdentifierSchema,
    type: FundTransactionTypeSchema,
    principalDeltaFen: FundSafeIntegerSchema,
    bonusDeltaFen: FundSafeIntegerSchema,
    principalBalanceFen: FundBalanceSchema,
    bonusBalanceFen: FundBalanceSchema,
    entries: Type.Array(FundTransactionReadEntrySchema, { maxItems: 2_000 }),
    priceSnapshotFen: FundBalanceSchema,
    walletCoveredAmountFen: FundBalanceSchema,
    externalAmountFen: FundBalanceSchema,
    orderNumber: Type.Union([OrderNumberSchema, Type.Null()]),
    rechargeId: FundNullableIdentifierSchema,
    originalTransactionId: FundNullableIdentifierSchema,
    reason: Type.String({ minLength: 1, maxLength: 200 }),
    operatorDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    authorizerDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 80 }), Type.Null()]),
    occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
    dataSource: Type.Union([Type.Literal("TEST_DATA"), Type.Literal("STORE_RECORD")]),
  },
  { additionalProperties: false },
);
const MemberFundTransactionRecordListBoundarySchema = Type.Array(
  MemberFundTransactionRecordBoundarySchema,
  { maxItems: 500 },
);
const MemberFundTransactionReadModelListSchema = Type.Array(MemberFundTransactionReadModelSchema, {
  maxItems: 500,
});
const fundReadMemberIdPattern = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u;

type FundTransactionSemanticShape = Pick<
  MemberFundTransactionReadModel,
  | "id"
  | "memberId"
  | "type"
  | "principalDeltaFen"
  | "bonusDeltaFen"
  | "entries"
  | "priceSnapshotFen"
  | "walletCoveredAmountFen"
  | "externalAmountFen"
  | "orderNumber"
  | "rechargeId"
  | "originalTransactionId"
  | "occurredAt"
> & { orderId?: string | null };

function fundTransactionHasOrderLink(value: FundTransactionSemanticShape): boolean {
  return "orderId" in value
    ? value.orderId !== null
    : value.type === "CONSUMPTION" || value.type === "REFUND";
}

function fundTransactionOrderReference(value: FundTransactionSemanticShape): string | null {
  return "orderId" in value ? (value.orderId ?? null) : value.orderNumber;
}

function fundOrderNumberMatchesOrderId(orderNumber: OrderNumber | null, orderId: string | null) {
  if (orderNumber === null) return true;
  if (orderId === null || !isOrderNumber(orderNumber)) return false;
  const compactDate = orderNumber.slice(3, 11);
  const businessDate = `${compactDate.slice(0, 4)}-${compactDate.slice(4, 6)}-${compactDate.slice(6, 8)}`;
  try {
    return deriveOrderNumber(businessDate, orderId) === orderNumber;
  } catch {
    return false;
  }
}

function isCanonicalFundTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function fundTransactionSemanticsAreConsistent(value: FundTransactionSemanticShape): boolean {
  if (!isCanonicalFundTimestamp(value.occurredAt)) return false;
  const walletIds = new Set<string>();
  let coveredAmountFen = 0;
  for (const entry of value.entries) {
    if (
      walletIds.has(entry.walletDefinitionId) ||
      entry.delta === 0 ||
      entry.balanceAfter !== entry.balanceBefore + entry.delta ||
      !Number.isSafeInteger(entry.balanceAfter) ||
      (entry.unit === "CUSTOM") !== (entry.customUnitLabel !== null) ||
      (entry.coveredAmountFen !== null && entry.coveredAmountFen <= 0)
    ) {
      return false;
    }
    walletIds.add(entry.walletDefinitionId);
    coveredAmountFen += entry.coveredAmountFen ?? 0;
    if (!Number.isSafeInteger(coveredAmountFen)) return false;
  }
  if (coveredAmountFen !== value.walletCoveredAmountFen) return false;

  const positiveEntries = value.entries.every((entry) => entry.delta > 0);
  const negativeEntries = value.entries.every((entry) => entry.delta < 0);
  const uncoveredEntries = value.entries.every((entry) => entry.coveredAmountFen === null);
  const coveredEntries = value.entries.every((entry) => entry.coveredAmountFen !== null);
  const hasOrderLink = fundTransactionHasOrderLink(value);
  if (value.type === "RECHARGE") {
    return (
      !hasOrderLink &&
      value.orderNumber === null &&
      value.rechargeId !== null &&
      value.originalTransactionId === null &&
      value.entries.length > 0 &&
      positiveEntries &&
      uncoveredEntries &&
      value.principalDeltaFen >= 0 &&
      value.bonusDeltaFen >= 0 &&
      value.priceSnapshotFen > 0 &&
      value.walletCoveredAmountFen === 0 &&
      value.externalAmountFen === 0
    );
  }
  if (value.type === "CONSUMPTION") {
    return (
      hasOrderLink &&
      value.rechargeId === null &&
      value.originalTransactionId === null &&
      negativeEntries &&
      coveredEntries &&
      value.principalDeltaFen <= 0 &&
      value.bonusDeltaFen <= 0 &&
      value.priceSnapshotFen > 0 &&
      value.priceSnapshotFen === value.walletCoveredAmountFen + value.externalAmountFen &&
      Number.isSafeInteger(value.priceSnapshotFen)
    );
  }
  if (value.type === "REFUND") {
    return (
      hasOrderLink &&
      value.rechargeId === null &&
      value.originalTransactionId !== null &&
      value.entries.length > 0 &&
      positiveEntries &&
      coveredEntries &&
      value.principalDeltaFen >= 0 &&
      value.bonusDeltaFen >= 0 &&
      value.priceSnapshotFen > 0 &&
      value.priceSnapshotFen === value.walletCoveredAmountFen &&
      value.externalAmountFen === 0
    );
  }
  return (
    !hasOrderLink &&
    value.orderNumber === null &&
    value.rechargeId === null &&
    value.originalTransactionId === null &&
    value.entries.length > 0 &&
    uncoveredEntries &&
    value.priceSnapshotFen === 0 &&
    value.walletCoveredAmountFen === 0 &&
    value.externalAmountFen === 0
  );
}

function fundTransactionListSemanticsAreConsistent(
  value: readonly FundTransactionSemanticShape[],
  expectedMemberId: string,
): boolean {
  const transactionIds = new Set<string>();
  let previousOccurredAt = Number.POSITIVE_INFINITY;
  for (const transaction of value) {
    const occurredAt = Date.parse(transaction.occurredAt);
    if (
      transaction.memberId !== expectedMemberId ||
      transactionIds.has(transaction.id) ||
      !fundTransactionSemanticsAreConsistent(transaction) ||
      occurredAt > previousOccurredAt
    ) {
      return false;
    }
    transactionIds.add(transaction.id);
    previousOccurredAt = occurredAt;
  }
  const byId = new Map(value.map((transaction) => [transaction.id, transaction]));
  const refundedByOriginal = new Map<string, number>();
  for (const transaction of value) {
    if (transaction.type !== "REFUND" || transaction.originalTransactionId === null) continue;
    const original = byId.get(transaction.originalTransactionId);
    if (
      original &&
      (original.type !== "CONSUMPTION" ||
        (fundTransactionOrderReference(original) !== null &&
          fundTransactionOrderReference(transaction) !== null &&
          fundTransactionOrderReference(original) !== fundTransactionOrderReference(transaction)) ||
        Date.parse(original.occurredAt) > Date.parse(transaction.occurredAt))
    ) {
      return false;
    }
    const refunded =
      (refundedByOriginal.get(transaction.originalTransactionId) ?? 0) +
      transaction.walletCoveredAmountFen;
    if (!Number.isSafeInteger(refunded)) return false;
    refundedByOriginal.set(transaction.originalTransactionId, refunded);
  }
  for (const [originalId, refunded] of refundedByOriginal) {
    const original = byId.get(originalId);
    if (original && refunded > original.walletCoveredAmountFen) return false;
  }
  return true;
}

export function isMemberFundTransactionRecordList(
  value: unknown,
  expectedMemberId: string,
): value is MemberFundTransactionRecord[] {
  if (
    !fundReadMemberIdPattern.test(expectedMemberId) ||
    !Value.Check(MemberFundTransactionRecordListBoundarySchema, value)
  ) {
    return false;
  }
  const transactions = value as Array<
    Omit<MemberFundTransactionRecord, "entries"> &
      Required<
        Pick<
          MemberFundTransactionRecord,
          | "paymentRuleId"
          | "paymentRuleVersion"
          | "targetType"
          | "targetId"
          | "priceSnapshotFen"
          | "walletCoveredAmountFen"
          | "externalAmountFen"
          | "ruleApplications"
        >
      > & {
        entries: Array<
          WalletLedgerEntryRecord &
            Required<
              Pick<
                WalletLedgerEntryRecord,
                "customUnitLabel" | "precision" | "refundableAtTransaction"
              >
            >
        >;
      }
  >;
  for (const transaction of transactions) {
    if (
      transaction.entries.some(
        (entry) => entry.transactionId !== transaction.id || entry.id.length === 0,
      ) ||
      !fundOrderNumberMatchesOrderId(transaction.orderNumber, transaction.orderId) ||
      (transaction.paymentRuleId === null) !== (transaction.paymentRuleVersion === null) ||
      (transaction.targetType === null && transaction.targetId !== null) ||
      ((transaction.type === "RECHARGE" || transaction.type === "ADJUSTMENT") &&
        (transaction.paymentRuleId !== null ||
          transaction.targetType !== null ||
          transaction.targetId !== null ||
          transaction.ruleApplications.length > 0))
    ) {
      return false;
    }
    let applicationAmountFen = 0;
    let applicationWalletFen = 0;
    let applicationExternalFen = 0;
    for (const application of transaction.ruleApplications) {
      if (
        application.amountFen !==
          application.walletCoveredAmountFen + application.externalAmountFen ||
        (application.targetType === "ORDER" && application.targetId !== null) ||
        (application.targetType !== "ORDER" && application.targetId === null)
      ) {
        return false;
      }
      applicationAmountFen += application.amountFen;
      applicationWalletFen += application.walletCoveredAmountFen;
      applicationExternalFen += application.externalAmountFen;
      if (
        !Number.isSafeInteger(applicationAmountFen) ||
        !Number.isSafeInteger(applicationWalletFen) ||
        !Number.isSafeInteger(applicationExternalFen)
      ) {
        return false;
      }
    }
    if (
      transaction.ruleApplications.length > 0 &&
      (applicationAmountFen !== transaction.priceSnapshotFen ||
        applicationWalletFen !== transaction.walletCoveredAmountFen ||
        applicationExternalFen !== transaction.externalAmountFen)
    ) {
      return false;
    }
  }
  return fundTransactionListSemanticsAreConsistent(transactions, expectedMemberId);
}

export function isMemberFundTransactionReadModelList(
  value: unknown,
  expectedMemberId: string,
): value is MemberFundTransactionReadModel[] {
  return (
    fundReadMemberIdPattern.test(expectedMemberId) &&
    Value.Check(MemberFundTransactionReadModelListSchema, value) &&
    value.every(
      (transaction) => transaction.orderNumber === null || isOrderNumber(transaction.orderNumber),
    ) &&
    fundTransactionListSemanticsAreConsistent(value, expectedMemberId)
  );
}

export interface MemberDashboard {
  activeMembers: number;
  frozenMembers: number;
  totalAvailableBalanceFen: number;
  pendingRechargeCount: number;
  todayRechargeFen: number;
  todayConsumptionFen: number;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const MemberDashboardSchema = Type.Object(
  {
    activeMembers: MemberReadCountSchema,
    frozenMembers: MemberReadCountSchema,
    totalAvailableBalanceFen: MemberReadMoneySchema,
    pendingRechargeCount: MemberReadCountSchema,
    todayRechargeFen: MemberReadMoneySchema,
    todayConsumptionFen: MemberReadMoneySchema,
    evidence: Type.Union([Type.Literal("SIMULATED"), Type.Literal("STORE_ACCEPTED")]),
  },
  { additionalProperties: false },
);

export function isMemberDashboard(value: unknown): value is MemberDashboard {
  return Value.Check(MemberDashboardSchema, value);
}

export const GroupBuyProviderSchema = Type.Union([Type.Literal("MEITUAN"), Type.Literal("DOUYIN")]);
export type GroupBuyProvider = Static<typeof GroupBuyProviderSchema>;

export const GroupBuyChannelUpsertRequestSchema = Type.Object(
  {
    provider: GroupBuyProviderSchema,
    displayName: Type.String({ minLength: 1, maxLength: 40 }),
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type GroupBuyChannelUpsertRequest = Static<typeof GroupBuyChannelUpsertRequestSchema>;

export interface GroupBuyChannelRecord {
  id: string;
  provider: GroupBuyProvider;
  displayName: string;
  fulfillmentMode: "MANUAL_PLATFORM_APP";
  officialIntegration: "NOT_VERIFIED";
  enabled: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "MANUAL_FALLBACK";
}

export const GroupBuyPackageComponentSchema = Type.Object(
  {
    kind: Type.Union([Type.Literal("PRODUCT"), Type.Literal("SERVICE")]),
    referenceId: Type.Union([Type.String({ minLength: 1, maxLength: 64 }), Type.Null()]),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    quantity: Type.Integer({ minimum: 1, maximum: 99 }),
  },
  { additionalProperties: false },
);
export type GroupBuyPackageComponent = Static<typeof GroupBuyPackageComponentSchema>;

export const GroupBuyPackageUpsertRequestSchema = Type.Object(
  {
    channelId: Type.String({ minLength: 1, maxLength: 64 }),
    name: Type.String({ minLength: 1, maxLength: 80 }),
    includedMinutes: Type.Integer({ minimum: 0, maximum: 1_440 }),
    components: Type.Array(GroupBuyPackageComponentSchema, { maxItems: 30 }),
    voucherCoveredFen: MoneyFenSchema,
    customerSupplementFen: MoneyFenSchema,
    expectedSettlementFen: MoneyFenSchema,
    expectedFeeFen: MoneyFenSchema,
    expectedSubsidyFen: MoneyFenSchema,
    enabled: Type.Boolean(),
    expectedVersion: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type GroupBuyPackageUpsertRequest = Static<typeof GroupBuyPackageUpsertRequestSchema>;

export interface GroupBuyPackageRecord
  extends Omit<GroupBuyPackageUpsertRequest, "expectedVersion" | "idempotencyKey"> {
  id: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  evidence: "SIMULATED" | "STORE_ACCEPTED";
}

export const RecordGroupBuyFulfillmentRequestSchema = Type.Object(
  {
    sessionId: Type.String({ minLength: 1, maxLength: 128 }),
    channelId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedChannelVersion: Type.Integer({ minimum: 1 }),
    packageId: Type.String({ minLength: 1, maxLength: 64 }),
    expectedPackageVersion: Type.Integer({ minimum: 1 }),
    platformReference: Type.String({ minLength: 4, maxLength: 64 }),
    voucherCoveredFen: MoneyFenSchema,
    customerSupplementFen: MoneyFenSchema,
    expectedSettlementFen: MoneyFenSchema,
    expectedFeeFen: MoneyFenSchema,
    expectedSubsidyFen: MoneyFenSchema,
    note: Type.String({ maxLength: 200 }),
    operatorMode: Type.Optional(GroupBuyManualOperatorModeSchema),
    exceptionReason: Type.Optional(
      Type.Union([
        Type.String({
          minLength: 2,
          maxLength: 500,
          pattern: "^\\S(?:[\\s\\S]*\\S)?$",
        }),
        Type.Null(),
      ]),
    ),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordGroupBuyFulfillmentRequest = Static<
  typeof RecordGroupBuyFulfillmentRequestSchema
>;

export function isRecordGroupBuyFulfillmentRequest(
  value: unknown,
): value is RecordGroupBuyFulfillmentRequest {
  return Value.Check(RecordGroupBuyFulfillmentRequestSchema, value);
}

export const ReconcileGroupBuyFulfillmentRequestSchema = Type.Object(
  {
    outcome: Type.Union([
      Type.Literal("CONFIRMED"),
      Type.Literal("NOT_SETTLED"),
      Type.Literal("AMOUNT_MISMATCH"),
      Type.Literal("DUPLICATE_RECORD"),
    ]),
    actualSettlementFen: MoneyFenSchema,
    actualFeeFen: MoneyFenSchema,
    actualSubsidyFen: MoneyFenSchema,
    note: Type.String({ minLength: 2, maxLength: 500 }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ReconcileGroupBuyFulfillmentRequest = Static<
  typeof ReconcileGroupBuyFulfillmentRequestSchema
>;

export interface GroupBuyFulfillmentRecord {
  id: string;
  orderId: string;
  sessionId: string;
  tableId: string;
  tableNumber: string;
  businessDate: string;
  channelSnapshot: {
    id: string;
    provider: GroupBuyProvider;
    displayName: string;
    version: number;
    fulfillmentMode: "MANUAL_PLATFORM_APP" | "ONLINE_PROVIDER";
    officialIntegration: "NOT_VERIFIED" | "SANDBOX" | "STORE_ACCEPTED";
  };
  packageSnapshot: Pick<
    GroupBuyPackageRecord,
    "id" | "name" | "version" | "includedMinutes" | "components"
  >;
  inventoryEffects?: Array<{
    skuId: string;
    productName: string;
    quantity: number;
    inventoryManaged: boolean;
    inventoryQuantityBefore: number | null;
    inventoryQuantityAfter: number | null;
  }>;
  platformReference: string;
  redemptionId?: string;
  redemptionNumber?: string;
  redemptionEntryMode?: "ONLINE" | "OFFLINE_PRECHECK";
  voucherCoveredFen: number;
  customerSupplementFen: number;
  expectedSettlementFen: number;
  expectedFeeFen: number;
  expectedSubsidyFen: number;
  actualSettlementFen: number | null;
  actualFeeFen: number | null;
  actualSubsidyFen: number | null;
  reconciliationOutcome:
    | "PENDING"
    | "CONFIRMED"
    | "NOT_SETTLED"
    | "AMOUNT_MISMATCH"
    | "DUPLICATE_RECORD";
  note: string;
  reconciliationNote: string | null;
  coverageStartedAt: string;
  coverageEndsAt: string;
  fulfilledAt: string;
  reconciledAt: string | null;
  actorId: string;
  authorizerId: string | null;
  operatorDecision?: GroupBuyOperatorDecision;
  evidence: "MANUAL_FALLBACK" | "SANDBOX" | "STORE_ACCEPTED";
}

export type GroupBuyManualFulfillmentOperatorDecision = GroupBuyManualOperatorDecision;

const GroupBuyReconciliationCountSchema = Type.Integer({ minimum: 0, maximum: 10_000 });
const GroupBuyReconciliationMoneyFenSchema = Type.Integer({
  minimum: 0,
  maximum: 1_000_000_000_000,
});

export const GroupBuyChannelReconciliationRowSchema = Type.Object(
  {
    provider: GroupBuyProviderSchema,
    channelName: Type.String({ minLength: 1, maxLength: 80 }),
    fulfillmentCount: GroupBuyReconciliationCountSchema,
    orderCount: GroupBuyReconciliationCountSchema,
    voucherCoveredFen: GroupBuyReconciliationMoneyFenSchema,
    customerSupplementFen: GroupBuyReconciliationMoneyFenSchema,
    expectedSettlementFen: GroupBuyReconciliationMoneyFenSchema,
    actualSettlementFen: GroupBuyReconciliationMoneyFenSchema,
    expectedFeeFen: GroupBuyReconciliationMoneyFenSchema,
    actualFeeFen: GroupBuyReconciliationMoneyFenSchema,
    expectedSubsidyFen: GroupBuyReconciliationMoneyFenSchema,
    actualSubsidyFen: GroupBuyReconciliationMoneyFenSchema,
    pendingCount: GroupBuyReconciliationCountSchema,
    exceptionCount: GroupBuyReconciliationCountSchema,
  },
  { additionalProperties: false },
);
export type GroupBuyChannelReconciliationRow = Static<
  typeof GroupBuyChannelReconciliationRowSchema
>;

export const GroupBuyReconciliationSummarySchema = Type.Object(
  {
    businessDate: SummaryBusinessDateSchema,
    channels: Type.Array(GroupBuyChannelReconciliationRowSchema, { minItems: 2, maxItems: 2 }),
    evidence: Type.Literal("MANUAL_FALLBACK"),
    officialIntegration: Type.Literal("NOT_VERIFIED"),
  },
  { additionalProperties: false },
);
export type GroupBuyReconciliationSummary = Static<typeof GroupBuyReconciliationSummarySchema>;

export function isGroupBuyReconciliationSummary(
  value: unknown,
  expectedBusinessDate?: string,
): value is GroupBuyReconciliationSummary {
  if (
    !Value.Check(GroupBuyReconciliationSummarySchema, value) ||
    !isCanonicalSummaryBusinessDate(value.businessDate) ||
    (expectedBusinessDate !== undefined && value.businessDate !== expectedBusinessDate) ||
    new Set(value.channels.map((channel) => channel.provider)).size !== value.channels.length ||
    !value.channels.some((channel) => channel.provider === "MEITUAN") ||
    !value.channels.some((channel) => channel.provider === "DOUYIN")
  ) {
    return false;
  }

  return value.channels.every((channel) => {
    if (
      channel.orderCount > channel.fulfillmentCount ||
      channel.pendingCount + channel.exceptionCount > channel.fulfillmentCount ||
      !Number.isSafeInteger(channel.pendingCount + channel.exceptionCount)
    ) {
      return false;
    }
    if (channel.fulfillmentCount > 0) return channel.orderCount > 0;
    return [
      channel.orderCount,
      channel.voucherCoveredFen,
      channel.customerSupplementFen,
      channel.expectedSettlementFen,
      channel.actualSettlementFen,
      channel.expectedFeeFen,
      channel.actualFeeFen,
      channel.expectedSubsidyFen,
      channel.actualSubsidyFen,
      channel.pendingCount,
      channel.exceptionCount,
    ].every((field) => field === 0);
  });
}
